import React, { useState, useEffect } from 'react';
import { notifications$ } from '../../services/subjects';
import { Alert, Stack, Slide, AlertProps } from '@mui/material';

const AUTO_HIDE_DURATION_IN_MS = 5000;
const ANIMATION_DURATION = 300;

export type Toast = {
    id: string;
    message: string;
    severity?: AlertProps['severity'];
    timestamp: number;
    visible: boolean;
}

export function Toasts(): JSX.Element {
    const [notifications, setNotifications] = useState<Toast[]>([]);

    useEffect(() => {
        const subscription = notifications$.subscribe((notification) => {
            const newNotification: Toast = {
                id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
                message: notification.message,
                severity: notification.severity || 'info',
                timestamp: Date.now(),
                visible: true
            };

            setNotifications(latest => [...latest, newNotification]);

            setTimeout(() => {
                setNotifications(latest => 
                    latest.map(notification => 
                        notification.id === newNotification.id 
                            ? { ...notification, visible: false }
                            : notification
                    )
                );
                
                setTimeout(() => {
                    setNotifications(prev => 
                        prev.filter(n => n.id !== newNotification.id)
                    );
                }, ANIMATION_DURATION);
            }, AUTO_HIDE_DURATION_IN_MS);
        });

        return () => subscription.unsubscribe();
    }, []);

    const handleClose = (notificationId: string) => {
        setNotifications(latest => 
            latest.map(notification => 
                notification.id === notificationId 
                    ? { ...notification, visible: false }
                    : notification
            )
        );
        
        setTimeout(() => {
            setNotifications(latest => 
                latest.filter(n => n.id !== notificationId)
            );
        }, ANIMATION_DURATION);
    };

    return (
        <Stack
            spacing={1}
            sx={{
                position: 'fixed',
                top: 16,
                right: 16,
                zIndex: 1400,
                maxWidth: 400,
                pointerEvents: 'none',
            }}
        >
            {notifications.map((notification) => (
                <Slide
                    key={notification.id}
                    direction="left"
                    in={notification.visible}
                    timeout={ANIMATION_DURATION}
                >
                    <Alert
                        severity={notification.severity}
                        onClose={() => handleClose(notification.id)}
                        aria-atomic="true"
                        sx={{ 
                            minWidth: 300,
                            maxWidth: 400,
                            wordBreak: 'break-word',
                            pointerEvents: 'auto',
                            boxShadow: 2,
                            '&:hover': {
                                boxShadow: 4,
                            }
                        }}
                    >
                        {notification.message}
                    </Alert>
                </Slide>
            ))}
        </Stack>
    );
}

export default Toasts;
